import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { FindAllMerchantsSpendAdminQueryQuery, QueryFindAllMerchantsSpendAdminArgs } from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllMerchantsSpendAdminQuery = gql`
  query FindAllMerchantsSpendAdminQuery(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllMerchantsSpendAdmin(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        userId
        userIsDeactivated
        adminId
        adminEmail
        merchantId
        username
        companyName
        businessName
        totalReceived
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllMerchantsSpendAdmin {
  data: FindAllMerchantsSpendAdminQueryQuery
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreMerchants: boolean;
}

interface Props {
  variables: QueryFindAllMerchantsSpendAdminArgs;
  children: (data: WithFindAllMerchantsSpendAdmin) => React.ReactElement | null;
}

export const FindAllMerchantsSpendAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllMerchantsSpendAdminQueryQuery,
    QueryFindAllMerchantsSpendAdminArgs
  >(findAllMerchantsSpendAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllMerchantsSpendAdminData = data?.findAllMerchantsSpendAdmin?.data || [];
  const page = data?.findAllMerchantsSpendAdmin?.page || variables.page;
  const pageSize = data?.findAllMerchantsSpendAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllMerchantsSpendAdmin?.total || 0;
  const totalPages = data?.findAllMerchantsSpendAdmin?.totalPages || 0;
  const hasMoreMerchants = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreMerchants) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllMerchantsSpendAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllMerchantsSpendAdmin: {
            ...fetchMoreResult?.findAllMerchantsSpendAdmin,
            data: [
              ...(prev?.findAllMerchantsSpendAdmin?.data || []),
              ...(fetchMoreResult?.findAllMerchantsSpendAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreMerchants]);

  return children({
    data: findAllMerchantsSpendAdminData as any,
    loading,
    loadMore,
    hasMoreMerchants,
    page,
    pageSize,
    total,
    totalPages,
  });
};